import { graphHooks } from '@tunasong/graph-lib/react'
import { matchAll } from '@tunasong/models'
import type { Entity, Persisted, UserSettings, UserSettingsData } from '@tunasong/schemas'
import { deterministicShortUuid, isEntity, isPersisted } from '@tunasong/schemas'
import { useEffect, useState } from 'react'
import { useCurrentUser } from '@tunasong/redux'

/**
 * GH #1635 / TUNASONG-W6/W7: `usersettings` rows this tab is creating or has just created, keyed on
 * `${parentId}:${userId}`. `usersettings` is a singleton per (parent, user), but the guard used to be a
 * per-hook-instance ref — so when two surfaces mounted `useUserSettings` for the same entity before settings
 * existed (e.g. the transpose sync + another panel in the same tab), both passed the read-before-create check and
 * each called `createEntity`, producing duplicate rows that then made `useEntityChild` flap/spam Sentry. A
 * module-level entry is shared across every mounted instance, so concurrent creates collapse to one.
 *
 * The entry also carries the writes made before the child query returns the new row: while the create is in
 * flight (sent once it lands), and after it landed but before the refetch brings the row back (sent at once).
 * Dropping them lost the second change of a visit: "This song only", then a part, saved only the first. The
 * entry is removed once the child query returns the row.
 *
 * This closes the same-tab multi-surface race. The cross-tab / cross-device race
 * is closed by `userSettingsId` below.
 */
interface CreatedSettings {
  id: string
  settings: UserSettingsData
  isCreating: boolean
}
const createdSettings = new Map<string, CreatedSettings>()

/**
 * GH #1635: the id a new `usersettings` child is created under, derived from (parent, user). Two tabs or devices
 * that both find no settings create under the SAME id, and the store's conditional create (`attribute_not_exists(id)`)
 * rejects the second with a 409 instead of storing a duplicate row. Existing rows keep their random ids; they are
 * still found by the type + owner filter, so no migration is needed.
 */
const userSettingsId = (parentId: string, userId: string) =>
  deterministicShortUuid(`usersettings:${parentId}:${userId}`)

/**
 * The create lost to a create of the same id: 409 'duplicate-entity' from an EntityStore, or the 500
 * 'ConditionalCheckFailed' of a deployment that predates it (same test as `classifyReplayResult` in @tunasong/redux).
 */
const isDuplicateCreateError = (error: unknown) => {
  if (typeof error !== 'object' || error === null) {
    return false
  }
  const { status, data } = error as { status?: unknown; data?: unknown }
  const body = JSON.stringify(data ?? null)
  return (
    (status === 409 && body.includes('duplicate-entity')) || (status === 500 && body.includes('ConditionalCheckFailed'))
  )
}

/** Get the child entity matching the filter. If there are > 1 children matching the critiera this hook will throw */
export interface UserSettingsOptions {
  /**
   * The server refused a save (Joni's review of #2936): the settings show the stored row again, so a switch the member
   * just flipped goes back, and the caller can say so.
   */
  onSaveError?: () => void
}

export const useUserSettings = (entity?: Persisted<Entity>, options: UserSettingsOptions = {}) => {
  const { onSaveError } = options
  const { userId } = useCurrentUser()
  /** Settings are private, so we need to filter for the userId */
  const filter = matchAll(
    e => isEntity(e) && e.type === 'usersettings',
    e => isPersisted(e) && e.userId === userId
  )
  /** Load settings once, then just update */
  const [localUserSettings, setLocalUserSettings] = useState<UserSettingsData | null>(null)

  const { child, isSuccess, isFetching } = graphHooks.useEntityChild<Persisted<UserSettings>>({
    parentId: entity?.id,
    filter,
    /**
     * Duplicate-creation is guarded by the module-level `creatingSettings` lock below
     * (GH #1635); `useEntityChild` itself takes only `{ parentId, filter }`.
     */
  })

  const { createEntity } = graphHooks.useEntityCreate()

  const shouldCreateSettings = isSuccess && !isFetching && !child && entity

  /** Clear settings when entity changes */
  useEffect(() => {
    setLocalUserSettings(null)
  }, [entity?.id])

  /** Update from child settings if localSettings is undefined (i.e., not initialized) */
  useEffect(() => {
    if (!isSuccess || localUserSettings !== null) {
      return
    }
    // Prioritize local settings over child settings
    // We need to avoid triggering a save here
    setLocalUserSettings(localSettings => ({ ...localSettings, ...child?.settings }))
  }, [child?.settings, isSuccess, localUserSettings])

  const isOwnRow = (id: string) =>
    id === child?.id || Boolean(entity && userId && id === userSettingsId(entity.id, userId))
  const updateEntity = graphHooks.useEntityUpdate<UserSettings>({
    debounceDelay: 100,
    /** True when the caller said so; otherwise the generic not-saved alert shows (transpose, Perform: GH #2950) */
    onError: ({ entityId }) => {
      if (!isOwnRow(entityId)) {
        return false
      }
      setLocalUserSettings({ ...child?.settings })
      if (!onSaveError) {
        return false
      }
      onSaveError()
      return true
    },
  })

  const createKey = entity && userId ? `${entity.id}:${userId}` : null

  /** The child query has returned the row: writes merge over it again */
  useEffect(() => {
    if (!createKey || !child?.id) {
      return
    }
    if (createdSettings.get(createKey)?.isCreating === false) {
      createdSettings.delete(createKey)
    }
  }, [createKey, child?.id])

  const createSettings = async (parent: Persisted<Entity>, settings: UserSettings['settings']) => {
    /** No signed-in user means no owner to derive the id from (and the private-settings filter matches nothing) */
    if (!userId || !createKey) {
      return
    }

    const created: CreatedSettings = { id: userSettingsId(parent.id, userId), settings, isCreating: true }
    createdSettings.set(createKey, created)

    try {
      await createEntity({
        parent,
        entity: { id: created.id, type: 'usersettings', settings } as UserSettings,
        isPrivate: true,
      })
    } catch (error) {
      if (!isDuplicateCreateError(error)) {
        createdSettings.delete(createKey)
        throw error
      }
      /** Another tab or device created the row first. It is this user's row (the id is derived from the user): write to it. */
      created.isCreating = false
      updateEntity(created.id, { settings: created.settings })
      return
    }
    created.isCreating = false
    /** Writes made while the create was in flight */
    if (created.settings !== settings) {
      updateEntity(created.id, { settings: created.settings })
    }
  }

  const setUserSettings = async (data: Partial<UserSettingsData>) => {
    /**
     * Merge over the freshest stored row, not this instance's copy: Perform mounts several instances on one row
     * (transpose sync, song parts), and each copies the row only once. Writing `{ ...localUserSettings, ...data }`
     * put another instance's newer key back to its stale value. A PATCH replaces `settings` whole.
     */
    const newSettings = { ...child?.settings, ...data }
    /** Local state also keeps values set before the row loaded (GH #1454); a create flushes them */
    const newLocalSettings = { ...localUserSettings, ...child?.settings, ...data }

    setLocalUserSettings(newLocalSettings)

    /** The row this tab created is not in the child query yet: write to it (or queue behind its create) */
    const created = createKey && !child?.id ? createdSettings.get(createKey) : undefined
    if (created) {
      created.settings = { ...created.settings, ...data }
      if (!created.isCreating) {
        updateEntity(created.id, { settings: created.settings })
      }
      return
    }

    if (shouldCreateSettings) {
      await createSettings(entity, newLocalSettings)
      return
    }

    /**
     * GH #1454 / TUNASONG-VW: reaching here means the `usersettings` child
     * hasn't resolved yet — the query is still loading (`!isSuccess`) or
     * refetching (`isFetching`), or there's no entity to attach to. The new
     * value is already captured in `localUserSettings` above, so skip the
     * remote write rather than asserting a child that doesn't exist yet.
     * Persisting before the query settles would either crash (the old
     * `invariant`, hit by tapping Transpose during initial load) or risk a
     * duplicate settings entity. A later settled `setUserSettings` flushes
     * it through the create branch; when a row already exists, the stored
     * row wins for every key the later write does not set.
     */
    if (!child?.id) {
      return
    }

    updateEntity(child.id, {
      settings: newSettings,
    })
  }

  return { userSettings: localUserSettings, setUserSettings, isLoaded: isSuccess }
}
